﻿import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { Box, Card, CardBody, CardHeader, Collapse, Flex, FlexProps, IconButton, Link, Progress, Text } from "@chakra-ui/react";
import { HTMLChakraProps } from "@chakra-ui/system";
import { DropzoneOptions, DropzoneState, useDropzone } from "react-dropzone";
import { IconCheck, IconCloudExclamation, IconX } from "@tabler/icons-react";
import { AnimationDefinition } from "framer-motion";
import { WidgetLoading } from "../WidgetLoading";
import { getRelativeFolderPath, getUniqueFolderPaths } from "../../util";

export interface UploadCallbacks<E = unknown> {
    progress: (e: Pick<ProgressEvent<EventTarget>, "lengthComputable" | "loaded" | "total">) => void;
    success: () => void;
    error: (e: E) => void;
}

export interface FileContainer<E = unknown> {
    index: number;
    file: File;
    relativeFolderPath: string;
    metaData?: Record<string, string>;
    callbacks: UploadCallbacks<E>;
}

export type BatchUploadedStats = { successCount: number; errorCount: number; cancelledCount: number };

interface AdditionalUploaderProps<E> {
    flattenFolders?: boolean;
    containerProps?: HTMLChakraProps<"div">;
    promptContent?: ReactNode;
    replaceContainerContent?: (dropzoneState: DropzoneState, uploads: Upload<E>[], dismiss: (index: number) => void) => ReactNode;
    containerUnstyled?: boolean;
    onDrop?: (fileContainers: Array<FileContainer<E>>, uniqueFolderPaths: string[]) => Promise<Array<FileContainer<E>>>;
    onFileAdded: (fileContainer: FileContainer<E>) => Promise<void>;
    onUploadError?: (fileContainer: FileContainer<E>, error: E) => void;
    onFileCancelled?: (fileContainer: FileContainer<E>) => void;
    onFileUploaded?: (fileContainer: FileContainer<E>, uploadsRemaining: number) => void;
    onBatchUploaded?: (stats: BatchUploadedStats) => void;
}

export type FileUploaderProps<E> = Omit<DropzoneOptions, "onDrop"> & AdditionalUploaderProps<E>;

interface Upload<E> extends FileContainer<E> {
    indeterminate: boolean;
    progress: number;
    success: boolean;
    error: boolean;
    show: boolean; // used to animate the upload out of view
    complete: boolean; // marked as complete when success or error is reached
    cancelled: boolean; // this is set when a user clicks the X on the upload before it starts uploading
}

export const FileUploader = <E,>({
    flattenFolders,
    containerProps,
    promptContent,
    replaceContainerContent,
    containerUnstyled,
    onDrop,
    onFileAdded,
    onUploadError,
    onFileCancelled,
    onFileUploaded,
    onBatchUploaded,
    multiple = true,
    ...dropzoneOptions
}: FileUploaderProps<E>) => {
    const [uploads, setUploads] = useState<Upload<E>[]>([]);
    const [isPreparing, setIsPreparing] = useState(false);
    const uploadsRef = useRef<Upload<E>[]>(uploads);

    const filesDropped = useCallback(async (files: File[]) => {
        const fileContainers: Array<FileContainer<E>> = [];
        for (const currentFile of files) {
            const uploadsTemp = [...uploadsRef.current];
            const currentUpload: Upload<E> = {
                index: multiple ? uploadsTemp.length : 0,
                file: currentFile,
                relativeFolderPath: flattenFolders ? "" : getRelativeFolderPath(currentFile),
                indeterminate: false,
                progress: 0,
                success: false,
                error: false,
                complete: false,
                show: true,
                cancelled: false,
                callbacks: {
                    progress: progressEvent => {
                        const uploadsCopy = [...uploadsRef.current];
                        const uploadCopy = { ...uploadsCopy[currentUpload.index] };
                        uploadCopy.progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                        uploadCopy.indeterminate = !progressEvent.lengthComputable;
                        uploadsCopy[currentUpload.index] = uploadCopy;
                        setUploads(uploadsCopy);
                        uploadsRef.current = uploadsCopy;
                    },
                    success: () => {
                        const uploadsCopy = [...uploadsRef.current];
                        const uploadCopy = { ...uploadsCopy[currentUpload.index] };
                        uploadCopy.success = true;
                        uploadCopy.complete = true;
                        uploadCopy.indeterminate = false;
                        uploadCopy.progress = 100;
                        uploadsCopy[currentUpload.index] = uploadCopy;
                        setUploads(uploadsCopy);
                        uploadsRef.current = uploadsCopy;

                        if (onFileUploaded || onBatchUploaded) {
                            const { index, file, relativeFolderPath, callbacks } = currentUpload;
                            const uploadsRemaining = uploadsCopy.filter(upload => !upload.complete).length;
                            if (onFileUploaded) {
                                onFileUploaded({ index, file, relativeFolderPath, callbacks }, uploadsRemaining);
                            }
                            if (uploadsRemaining === 0 && onBatchUploaded) {
                                const errorCount = uploadsCopy.filter(upload => upload.error).length;
                                const cancelledCount = uploadsCopy.filter(upload => upload.cancelled && !upload.error).length;
                                onBatchUploaded({ errorCount, cancelledCount, successCount: uploadsCopy.length - errorCount - cancelledCount });
                            }
                        }
                    },
                    error: (error: E) => {
                        const uploadsCopy = [...uploadsRef.current];
                        const uploadCopy = { ...uploadsCopy[currentUpload.index] };
                        uploadCopy.error = true;
                        uploadCopy.complete = true;
                        uploadCopy.indeterminate = false;
                        uploadCopy.progress = 0;
                        uploadsCopy[currentUpload.index] = uploadCopy;
                        setUploads(uploadsCopy);
                        uploadsRef.current = uploadsCopy;

                        if (onUploadError) {
                            const { index, file, relativeFolderPath, callbacks } = currentUpload;
                            onUploadError({ index, file, relativeFolderPath, callbacks }, error);
                        }
                    },
                },
            };
            if (multiple) {
                uploadsTemp.push(currentUpload);
            } else {
                uploadsTemp[0] = currentUpload;
            }
            setUploads(uploadsTemp);
            uploadsRef.current = uploadsTemp;

            const { index, file, relativeFolderPath, callbacks } = currentUpload;
            fileContainers.push({ index, file, relativeFolderPath, callbacks });
        }

        // call onDrop if provided
        let updatedFileContainers = fileContainers;
        if (onDrop) {
            setIsPreparing(true);
            updatedFileContainers = await onDrop(fileContainers, getUniqueFolderPaths(fileContainers));
            setIsPreparing(false);
        }

        // call onFileAdded for each fileContainer
        for (const fileContainer of updatedFileContainers) {
            await onFileAdded(fileContainer);
        }
    }, []);

    const hideAnimationComplete = (index: number) => {
        const uploadsCopy = [...uploadsRef.current];
        const uploadCopy = { ...uploadsCopy[index] };
        uploadCopy.show = false;
        uploadsCopy[index] = uploadCopy;
        setUploads(uploadsCopy);
        uploadsRef.current = uploadsCopy;
    };

    const cancel = (index: number) => {
        const uploadsCopy = [...uploadsRef.current];
        const uploadCopy = { ...uploadsCopy[index] };
        uploadCopy.cancelled = true;
        uploadCopy.complete = true;
        uploadsCopy[index] = uploadCopy;
        setUploads(uploadsCopy);
        uploadsRef.current = uploadsCopy;

        if (onFileCancelled) {
            const { index, file, relativeFolderPath, callbacks } = uploadCopy;
            onFileCancelled({ index, file, relativeFolderPath, callbacks });
        }
    };

    const shownUploads = useMemo(() => uploads.filter(upload => upload.show), [uploads]);
    const cancelledUploads = useMemo(() => uploads.filter(upload => upload.cancelled), [uploads]);
    const dropzoneState: DropzoneState = useDropzone({ disabled: shownUploads.length > cancelledUploads.length, onDrop: filesDropped, ...dropzoneOptions });
    const { getRootProps, getInputProps, isDragActive } = dropzoneState;

    const containerStyles: Partial<FlexProps> = useMemo(
        () =>
            containerUnstyled
                ? {}
                : {
                      direction: "column",
                      position: "relative",
                      justifyContent: shownUploads.length === 0 ? "center" : "start",
                      padding: "2rem",
                      width: "full",
                      minHeight: "6rem",
                      maxHeight: "50vh",
                      overflowY: "auto",
                      borderColor: isDragActive ? "orange.500" : "gray.200",
                      borderRadius: "lg",
                      borderWidth: "2px",
                      borderStyle: "dashed",
                      textAlign: "center",
                  },
        [containerUnstyled, shownUploads.length, isDragActive],
    );

    return (
        <Flex {...getRootProps()} {...containerStyles} {...containerProps}>
            {isPreparing && (
                <Box position={"absolute"} top={0} right={0} bottom={0} left={0} bg={"whiteAlpha.900"} zIndex={1}>
                    <Box height={"100%"} maxHeight={"12rem"}>
                        <WidgetLoading text={"Creating Remote Folders ..."} />
                    </Box>
                </Box>
            )}
            <input {...getInputProps()} />
            <Flex flexDirection={"column"} gap={"1rem"}>
                {replaceContainerContent ? (
                    replaceContainerContent(dropzoneState, shownUploads, hideAnimationComplete)
                ) : (
                    <>
                        {shownUploads.length === 0
                            ? promptContent || (
                                  <Text>
                                      Drag and drop files and folders here, or <Link textDecoration={"underline"}>click to select files from your device</Link>.
                                  </Text>
                              )
                            : shownUploads.map(upload => (
                                  <Collapse
                                      key={upload.index}
                                      in={!upload.complete || (upload.error && !upload.cancelled)}
                                      transition={{ exit: { delay: upload.cancelled ? 0 : 1.25, duration: 0.75 } }}
                                      onAnimationComplete={(definition: AnimationDefinition) => {
                                          if (definition === "exit") {
                                              hideAnimationComplete(upload.index);
                                          }
                                      }}
                                  >
                                      <Card>
                                          {!upload.indeterminate && !upload.progress && (
                                              <IconButton
                                                  variant={"ghost"}
                                                  aria-label={"Dismiss"}
                                                  icon={<IconX />}
                                                  position={"absolute"}
                                                  _hover={{ bg: "transparent" }}
                                                  top={0}
                                                  right={0}
                                                  onClick={event => {
                                                      event.stopPropagation();
                                                      cancel(upload.index);
                                                  }}
                                              />
                                          )}
                                          <CardBody>
                                              <CardHeader p={0} mb={".5rem"}>
                                                  <Flex>
                                                      <Box display={"flex"} gap={".5rem"}>
                                                          <Text variant={"label"}>{upload.file.name}</Text>
                                                          {upload.success && (
                                                              <Box color={"green.500"}>
                                                                  <IconCheck />
                                                              </Box>
                                                          )}
                                                          {upload.error && (
                                                              <Box color={"red.500"}>
                                                                  <IconCloudExclamation />
                                                              </Box>
                                                          )}
                                                      </Box>
                                                  </Flex>
                                              </CardHeader>
                                              <Progress
                                                  colorScheme={upload.error ? "red" : upload.complete ? "green" : "blue"}
                                                  size={"sm"}
                                                  isIndeterminate={upload.indeterminate}
                                                  value={upload.error ? 100 : upload.progress}
                                              />
                                          </CardBody>
                                      </Card>
                                  </Collapse>
                              ))}
                    </>
                )}
            </Flex>
        </Flex>
    );
};
